import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { trpc } from '@lib/trpc';
import { UploadResponse } from '@types';
import toast from 'react-hot-toast';

interface PdfUploadProps {
  onUploadSuccess: (response: UploadResponse) => void;
}

export const PdfUpload: React.FC<PdfUploadProps> = ({ onUploadSuccess }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const uploadPdfMutation = trpc.invoice.uploadPdf.useMutation({
    onSuccess: data => {
      setUploadStatus('success');
      onUploadSuccess(data as UploadResponse);
      toast.success('PDF processed successfully!');
    },
    onError: error => {
      setUploadStatus('error');
      toast.error(error.message || 'Failed to process PDF. Please try again.');
    },
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setIsUploading(true);
      setUploadStatus('idle');

      try {
        // Convert file to base64 string using FileReader (better for large files)
        const base64String = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Strip the "data:application/pdf;base64," prefix
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        await uploadPdfMutation.mutateAsync({
          file: base64String,
          filename: file.name,
        });
      } catch (error) {
        // Error is handled by the mutation
      } finally {
        setIsUploading(false);
      }
    },
    [uploadPdfMutation]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    disabled: isUploading,
  });

  return (
    <div className="w-full h-full">
      <div
        {...getRootProps()}
        className={`
          border-4 border-dashed rounded-lg shadow-lg p-8 text-center cursor-pointer transition-all duration-200 min-h-[320px] flex flex-col justify-center
          ${
            isDragActive
              ? 'border-accent-500 bg-accent-50'
              : 'border-secondary-300 hover:border-accent-400'
          }
          ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center space-y-4">
          {uploadStatus === 'success' ? (
            <CheckCircle className="w-12 h-12 text-success-500" />
          ) : uploadStatus === 'error' ? (
            <AlertCircle className="w-12 h-12 text-error-500" />
          ) : isUploading ? (
            <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload className="w-12 h-12 text-secondary-400" />
          )}

          <div>
            <h3 className="text-lg font-semibold text-secondary-800 mb-2">
              {isUploading
                ? 'Processing PDF...'
                : uploadStatus === 'success'
                  ? 'Upload Successful!'
                  : uploadStatus === 'error'
                    ? 'Upload Failed'
                    : 'Upload PDF Invoice'}
            </h3>

            <p className="text-secondary-600">
              {isUploading
                ? 'Please wait while we process your invoice...'
                : uploadStatus === 'success'
                  ? 'Your invoice has been processed successfully'
                  : uploadStatus === 'error'
                    ? 'There was an error processing your invoice'
                    : 'Drag and drop a PDF file here, or click to select'}
            </p>
          </div>

          {!isUploading && uploadStatus === 'idle' && (
            <div className="flex items-center space-x-2 text-sm text-secondary-500">
              <FileText className="w-4 h-4" />
              <span>Only PDF files are supported</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
